"use client";
import BodyLineText from "@/components/common/text/bodyline-text";
import HeadlineText from "@/components/common/text/headline-text";
import ScreenLayout from "@/components/layout/screen-layout";
import GradientButtonIcon from "@/components/ui/custom/gradient-button-icon";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { ImgAboutUsHero } from "@/lib/images";

function AboutHero() {
  return (
    <div className="relative min-h-[100vh] flex flex-col justify-center pt-32 md:pt-40 pb-16 overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-white">
      {/* Background Elements */}
      <div className="absolute inset-0 w-full h-full">
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-purple-400/20 to-blue-400/20 rounded-full blur-xl opacity-50" />
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl opacity-50" />
      </div>

      {/* Content Container */}
      <div className="relative z-10">
        <ScreenLayout>
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <div className="space-y-8">
                {/* Main Heading - Mobile styling */}
                <div className="lg:hidden">
                  <HeadlineText
                    wrapperClassName="flex justify-center lg:justify-start lg:max-w-150"
                    textClassName="lg:text-left items-center lg:items-start"
                  >
                    <span className="text-gray-900">Crafting Digital</span>{" "}
                    <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
                      Excellence
                    </span>{" "}
                    <span className="text-gray-900">Since Day One</span>
                  </HeadlineText>
                </div>

                {/* Main Heading - Desktop styling (unchanged) */}
                <div className="hidden lg:block space-y-4">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1]">
                    <span className="text-gray-900">Crafting Digital</span>{" "}
                    <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
                      Excellence
                    </span>{" "}
                    <span className="text-gray-900">Since Day One</span>
                  </h1>
                </div>

                {/* Subtitle - Mobile styling */}
                <div className="lg:hidden">
                  <BodyLineText
                    wrapperClassName="flex justify-start text-center lg:text-left"
                    textClassName="w-full opacity-80 text-base lg:text-[20px] xl:max-w-1/1"
                  >
                    We're Devkrest — a mighty team of designers, developers, and problem-solvers who love turning ambitious ideas into real, working products. We care about clean design, smooth user experiences, and smart technology that actually helps businesses grow.
                  </BodyLineText>
                </div>

                {/* Subtitle - Desktop styling (unchanged) */}
                <div className="hidden lg:block space-y-6">
                  <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                    We're Devkrest — a mighty team of designers, developers, and problem-solvers who love turning ambitious ideas into real, working products. We care about clean design, smooth user experiences, and smart technology that actually helps businesses grow.
                  </p>
                </div>

                {/* CTA Button - Mobile styling */}
                <div className="lg:hidden flex justify-center lg:justify-start pt-5">
                  <GradientButtonIcon href="/contact-us" icon={<ArrowUpRight className="size-4" />} className="w-full">
                    Build your product
                  </GradientButtonIcon>
                </div>

                {/* CTA Button - Desktop styling (unchanged) */}
                <div className="hidden lg:block pt-4">
                  <GradientButtonIcon href="/contact-us" icon={<ArrowUpRight className="size-5" />}>
                    Build your product
                  </GradientButtonIcon>
                </div>
              </div>

              {/* Right Side - Image */}
              <div className="flex justify-center lg:justify-end">
                <div className="relative w-full max-w-4xl lg:max-w-5xl">
                  <Image
                    src={ImgAboutUsHero}
                    alt="About Devkrest Technologies"
                    width={1000}
                    height={1000}
                    className="w-full h-auto object-cover scale-110"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
        </ScreenLayout>
      </div>
    </div>
  );
}

export default AboutHero;
